import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import SignatureCanvas from 'react-signature-canvas';
import { supabase } from '@/services';
import { useAuth } from '@/providers/auth-provider';
import { useToast } from '@/hooks/use-toast';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '@/providers/query-provider';
import { useTranslation } from 'react-i18next';
import { useUppy } from '@/providers/uppy-provider';
import { useUppyEvent, useUppyState } from '@uppy/react';
import { Progress } from './ui/progress';

type SignatureProps = {
  clientId: string;
  appointmentId: string;
  project_id: string;
};
const Signature = ({ clientId, appointmentId, project_id }: SignatureProps) => {
  const [sign, setSign] = useState<ReactSignatureCanvas | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const userId = user?.id;

  const deleteExistingSignature = async () => {
    if (!userId) return;

    try {
      const { error: deleteDbError } = await supabase
        .from('images')
        .delete()
        .eq('appointmentId', appointmentId)
        .eq('user_id', userId)
        .like('image_url', '%signatures%');

      if (deleteDbError) {
        console.error('Error deleting signature from database:', deleteDbError.message);
        throw new Error(
          'Error deleting signature from database: ' + deleteDbError.message
        );
      }

      const folderPath = `${userId}/signatures/${clientId}`;
      const { data: listData, error: listError } = await supabase.storage
        .from('appointments_images')
        .list(folderPath);

      if (listError) {
        console.error('Error listing files in signature folder:', listError.message);
        throw new Error('Error listing files in signature folder: ' + listError.message);
      }

      if (listData && listData.length > 0) {
        const filesToDelete = listData.map((file) => `${folderPath}/${file.name}`);
        const { error: deleteStorageError } = await supabase.storage
          .from('appointments_images')
          .remove(filesToDelete);

        if (deleteStorageError) {
          console.error('Error deleting files from storage:', deleteStorageError.message);
          throw new Error(
            'Error deleting files from storage: ' + deleteStorageError.message
          );
        }
      }
    } catch (error) {
      console.error('An error occurred while deleting existing signature:', error);
      toast({
        title: 'Error',
        description: 'Error deleting existing signature',
        variant: 'destructive',
      });
    }
  };

  const { uppy } = useUppy();
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: async (status: string) => {
      const { error } = await supabase
        .from('appointments')
        .update({ status })
        .eq('appointment_id', appointmentId);
      if (error) {
        throw new Error(error.message);
      }

      const { error: signError } = await supabase.functions.invoke('sign', {
        body: { appointment_id: appointmentId },
      });
      if (signError) {
        throw new Error(signError.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['appointment', appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ['appointments', userId],
      });
    },
    onError: (error) => {
      console.error('Error updating appointment status:', error);
      toast({
        title: 'Error',
        description: 'Error updating appointment status',
        variant: 'destructive',
      });
    },
  });

  const uploadSignature = async (fileName: string) => {
    setLoading(true);
    try {
      const { error: insertError } = await supabase.from('images').insert([
        {
          project_id,
          appointment_id: appointmentId,
          user_id: userId,
          image_url: fileName,
          type: 'signature',
        },
      ]);

      if (insertError) {
        console.error('Error inserting signature into database:', insertError.message);
        toast({
          title: 'Error',
          description: 'Error inserting signature into database',
          variant: 'destructive',
        });
      } else {
        await mutation.mutateAsync('Completed');
        toast({
          title: 'Success',
          description: 'Signature uploaded successfully',
        });
        sign?.clear();
        setIsOpen(false);
      }
    } catch (error) {
      console.error('An unknown error occurred:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlerClear = async () => {
    sign?.clear();

    await deleteExistingSignature();
  };

  const progress = useUppyState(uppy, (s) => s.totalProgress);

  useUppyEvent(uppy, 'file-added', (f) => {
    if (isOpen) {
      setLoading(true);
      f.meta = {
        ...f.meta,
        bucketName: import.meta.env.VITE_BUCKET_NAME,
        objectName: `${appointmentId}/${f.meta.name}`,
        contentType: f.type,
      };
    }
  });
  useUppyEvent(uppy, 'upload', () => {
    if (isOpen) {
      setLoading(true);
    }
  });
  useUppyEvent(uppy, 'complete', (r) => {
    if (isOpen) {
      const successfulUploads = r.successful;
      if (successfulUploads?.length) {
        const imageUrls = successfulUploads.map((file) => {
          const { bucketName, objectName } = file.meta;
          return `${bucketName}/${objectName}`;
        });
        uploadSignature(imageUrls[0]);
      }
    }
  });

  const handlerSave = async () => {
    const dataUrl = sign?.getSignaturePad().toDataURL();
    if (dataUrl) {
      const fileBlob = await fetch(dataUrl).then((res) => res.blob());
      uppy.addFile({
        name: 'signature.png',
        type: 'image/png',
        data: fileBlob,
      });
    }
  };
  const { t } = useTranslation();

  return (
    <div className="flex gap-2">
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button onClick={() => setIsOpen(true)} className=" mr-3 flex  gap-1">
            <IoIosAddCircleOutline size={20} /> {t('completeandsign')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('signature')}</DialogTitle>
            <DialogDescription>{t('pleasesignbelow')}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <SignatureCanvas
              ref={(data) => setSign(data)}
              canvasProps={{
                className:
                  'border border-gray-300 rounded w-full max-h-[300px] min-h-[200px] bg-white dark:bg-white/60',
              }}
            />
          </div>
          <div className="w-full flex flex-col items-center justify-center gap-4">
            {loading && <Spinner />}
            {loading && (
              <Progress value={progress} max={100} className="w-full h-4 mt-4" />
            )}
          </div>
          <div className="flex justify-end gap-4">
            <Button onClick={handlerClear} variant="destructive">
              {t('clear')}
            </Button>
            <Button onClick={handlerSave} disabled={loading}>
              {loading ? t('saving') : t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Signature;
